import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const ItAssetReportLayer = () => {
    const [itAssets, setItAssets] = useState([]);
    const [filteredAssets, setFilteredAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        category: '',
        condition: '',
        dateFrom: '',
        dateTo: '',
        location: '',
        department: '',
        assignedUserDepartment: '',
        serialNo: ''
    });

    useEffect(() => {
        fetchItAssets();
    }, []);

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, itAssets]);

    const fetchItAssets = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/it-assets?per_page=1000`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch IT assets: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                const assets = data.data.data || data.data;
                setItAssets(assets);
                setFilteredAssets(assets);
            } else {
                throw new Error(data.message || 'Failed to fetch IT assets');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...itAssets];

        // Category filter
        if (filters.category) {
            filtered = filtered.filter(asset => asset.category === filters.category);
        }

        // Condition filter
        if (filters.condition) {
            filtered = filtered.filter(asset => asset.condition === filters.condition);
        }

        // Date range filter
        if (filters.dateFrom) {
            filtered = filtered.filter(asset => 
                asset.date_acquired && new Date(asset.date_acquired) >= new Date(filters.dateFrom)
            );
        }

        if (filters.dateTo) {
            filtered = filtered.filter(asset => 
                asset.date_acquired && new Date(asset.date_acquired) <= new Date(filters.dateTo)
            );
        }

        // Location filter
        if (filters.location) {
            filtered = filtered.filter(asset => 
                asset.location?.toLowerCase().includes(filters.location.toLowerCase())
            );
        }

        // Department filter
        if (filters.department) {
            filtered = filtered.filter(asset => 
                asset.department?.toLowerCase().includes(filters.department.toLowerCase())
            );
        }

        // Assigned User / Department filter
        if (filters.assignedUserDepartment) {
            filtered = filtered.filter(asset => 
                asset.assigned_user_department?.toLowerCase().includes(filters.assignedUserDepartment.toLowerCase())
            );
        }

        // Serial Number filter
        if (filters.serialNo) {
            filtered = filtered.filter(asset => 
                asset.serial_number?.toLowerCase().includes(filters.serialNo.toLowerCase())
            );
        }

        setFilteredAssets(filtered);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            category: '',
            condition: '',
            dateFrom: '',
            dateTo: '',
            location: '',
            department: '',
            assignedUserDepartment: '',
            serialNo: ''
        });
    };

    const handleExportExcel = () => {
        // Prepare data for Excel
        const excelData = filteredAssets.map((asset, index) => ({
            'S.L': index + 1,
            'Tagging Number': asset.asset_id || '',
            'Category / Type': asset.category || '',
            'Brand / Model': asset.brand_model || '',
            'Serial Number': asset.serial_number || '',
            'Specifications / Description': asset.description || '',
            'Assigned User / Department': asset.assigned_user_department || '',
            'Location': asset.location || '',
            'Date Acquired / Purchase Date': asset.date_acquired ? new Date(asset.date_acquired).toISOString().split('T')[0] : '',
        }));

        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'IT Asset Report');

        // Set column widths
        const columnWidths = [
            { wch: 8 },   // S.L
            { wch: 18 },  // Tagging Number
            { wch: 20 },  // Category / Type
            { wch: 25 },  // Brand / Model
            { wch: 18 },  // Serial Number
            { wch: 40 },  // Specifications / Description
            { wch: 25 },  // Assigned User / Department
            { wch: 15 },  // Location
            { wch: 20 }   // Date Acquired / Purchase Date
        ];
        worksheet['!cols'] = columnWidths;

        // Generate Excel file and download
        const fileName = `IT_Asset_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    // Get unique categories and conditions for dropdowns
    const uniqueCategories = [...new Set(itAssets.map(asset => asset.category).filter(Boolean))];
    const uniqueConditions = [...new Set(itAssets.map(asset => asset.condition).filter(Boolean))];

    return (
        <div className='card h-100 p-0 radius-12'>
            <div className='card-body p-24'>
                {/* Filters Section */}
                <div className='card border mb-24'>
                    <div className='card-body p-24'>
                        <h5 className='mb-20 fw-semibold'>Filters</h5>
                        <div className='row gy-3'>
                            {/* Row 1 */}
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Category</label>
                                <select
                                    name='category'
                                    className='form-select'
                                    value={filters.category}
                                    onChange={handleFilterChange}
                                >
                                    <option value=''>All Categories</option>
                                    {uniqueCategories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Condition</label>
                                <select
                                    name='condition'
                                    className='form-select'
                                    value={filters.condition}
                                    onChange={handleFilterChange}
                                >
                                    <option value=''>All Conditions</option>
                                    {uniqueConditions.map(cond => (
                                        <option key={cond} value={cond}>{cond.charAt(0).toUpperCase() + cond.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Date From</label>
                                <input
                                    type='date'
                                    name='dateFrom'
                                    className='form-control'
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Date To</label>
                                <input
                                    type='date'
                                    name='dateTo'
                                    className='form-control'
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            {/* Row 2 */}
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Location</label>
                                <input
                                    type='text'
                                    name='location'
                                    className='form-control'
                                    placeholder='Search location...'
                                    value={filters.location}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Department</label>
                                <input
                                    type='text'
                                    name='department'
                                    className='form-control'
                                    placeholder='Search department...'
                                    value={filters.department}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Assigned User / Department</label>
                                <input
                                    type='text'
                                    name='assignedUserDepartment'
                                    className='form-control'
                                    placeholder='Search assigned user / department...'
                                    value={filters.assignedUserDepartment}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className='col-md-3'>
                                <label className='form-label fw-semibold text-sm mb-8'>Serial No.</label>
                                <input
                                    type='text'
                                    name='serialNo'
                                    className='form-control'
                                    placeholder='Search serial number...'
                                    value={filters.serialNo}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            {/* Reset Button */}
                            <div className='col-12 mt-3'>
                                <button
                                    type='button'
                                    onClick={handleResetFilters}
                                    className='btn btn-danger-600 radius-8 px-20 py-11 d-flex align-items-center gap-2'
                                >
                                    <Icon icon='mdi:close' className='text-xl' />
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Section */}
                <div className='card border'>
                    <div className='card-body p-24'>
                        {error && (
                            <div className='alert alert-danger mb-3' role='alert'>
                                {error}
                            </div>
                        )}

                        {/* Export Excel Button */}
                        <div className='d-flex justify-content-end mb-24'>
                            <button
                                type='button'
                                onClick={handleExportExcel}
                                className='btn btn-success-600 radius-8 px-20 py-11 d-flex align-items-center gap-2'
                                disabled={filteredAssets.length === 0}
                            >
                                <Icon icon='vscode-icons:file-type-excel' className='text-xl' />
                                Export Excel
                            </button>
                        </div>

                        {loading ? (
                            <div className='text-center py-5'>
                                <div className='spinner-border text-primary' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className='table-responsive scroll-sm'>
                                <table className='table bordered-table sm-table mb-0'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>S.L</th>
                                            <th scope='col'>Tagging Number</th>
                                            <th scope='col'>Category / Type</th>
                                            <th scope='col'>Brand / Model</th>
                                            <th scope='col'>Serial Number</th>
                                            <th scope='col'>Specifications / Description</th>
                                            <th scope='col'>Assigned User / Department</th>
                                            <th scope='col'>Location</th>
                                            <th scope='col'>Date Acquired / Purchase Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAssets.length === 0 ? (
                                            <tr>
                                                <td colSpan='9' className='text-center py-4'>
                                                    No assets found matching the filter criteria
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredAssets.map((asset, index) => (
                                                <tr key={asset.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <span className='badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm'>
                                                            {asset.asset_id}
                                                        </span>
                                                    </td>
                                                    <td>{asset.category || 'N/A'}</td>
                                                    <td>{asset.brand_model || 'N/A'}</td>
                                                    <td>{asset.serial_number || 'N/A'}</td>
                                                    <td>{asset.description || 'N/A'}</td>
                                                    <td>{asset.assigned_user_department || 'N/A'}</td>
                                                    <td>{asset.location || 'N/A'}</td>
                                                    <td>{formatDate(asset.date_acquired)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItAssetReportLayer;

